import { FC, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { SearchIcon } from '@heroicons/react/outline';
import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import { giftDeliveryCategoryListDataAtom } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import {
  giftItemForStoreDataAtomFamily,
  giftItemForStoreStatusAtomFamily,
  giftItemListDataAtom,
  giftItemListStatusAtom,
} from '@shared/api/giftItem/giftItem.atom';
import {
  useGiftItemForStoreHook,
  useGiftItemListHook,
} from '@shared/api/giftItem/giftItem.hook';
import { GiftItemData } from '@shared/api/giftItem/giftItem.interface';
import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { GiftItemPreviewModal } from '@shared/components/GiftItemPreviewModal';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatPrice } from '@shared/utils/formatPrice';

/**
 * 전화주문 관리 "상품 목록" 탭 — 전화 응대 직원용.
 *
 * 고객이 보는 스토어(GiftStoreMainPage)와 같은 흐름으로 배치한다:
 * 쿠폰형 먼저 → 배송형은 카테고리 → 하위분류 순, 카드 모양도 스토어와 동일.
 * 카드를 누르면 상품 상세페이지를 정보 위주로 축약한 모달이 열린다
 * (선택형이면 선택 가능한 상품까지 — 데이터는 고객 상세와 같은 public 단건 조회).
 *
 * 스토어 카탈로그(public/gift-item/by-bugo)는 부고가 있어야 열리므로 상품은
 * 관리자용 gift-item 목록을 쓰고(백엔드 무수정), 분류는 고객과 같은 public
 * 카테고리 목록을 쓴다. GiftAgent는 gift-agent/gift-item 선배선 — 개방 전 404 안내.
 */

const itemName = (item: GiftItemData) => item.name || item.giftItemInfo?.name || '';

//* 썸네일은 스토어와 같은 관습(fileBucketUrl w400)
const imageUrlOf = (image: unknown): string => {
  if (image && typeof image === 'object' && 'url' in image) {
    return fileBucketUrl((image as Image).url, 'w400');
  }
  return '';
};

const itemImageUrl = (item: GiftItemData): string =>
  imageUrlOf(item.image || item.giftItemInfo?.image);

const itemOrder = (item: GiftItemData) =>
  item.order ?? item.giftItemInfo?.order ?? Number.MAX_SAFE_INTEGER;

const itemType = (item: GiftItemData): GiftItemInfoType =>
  item.giftItemInfo?.type || GiftItemInfoType.Delivery;

//* 카테고리: 상품별 오버라이드 우선, 없으면 원상품 값 (스토어와 동일 규칙)
const itemCategoryId = (item: GiftItemData): string | undefined => {
  const override = item.giftDeliveryCategory;
  if (override) return typeof override === 'object' ? override._id : override;
  const fromInfo = item.giftItemInfo?.giftDeliveryCategory;
  if (fromInfo) return typeof fromInfo === 'object' ? fromInfo._id : fromInfo;
  return undefined;
};

const brandName = (item: GiftItemData): string => {
  const agency = item.bugoBrand?.bugoAgency;
  return agency?.nickName || agency?.corpName || '기타';
};

const discountRate = (priceRegular: number, priceRetail: number): number =>
  priceRegular > 0 ? Math.round(((priceRegular - priceRetail) / priceRegular) * 100) : 0;

//* 검색 대상 텍스트 — 상품명·카테고리·하위분류·태그·교환처·옵션·설명을 한 덩어리로.
//* 카테고리명은 populate 안 된 id일 수 있어 public 카테고리 목록에서 이름을 찾는다.
const buildSearchHaystack = (
  item: GiftItemData,
  categoryNameById: Map<string, string>,
): string => {
  const info = item.giftItemInfo;
  const categoryId = itemCategoryId(item);
  const categoryName =
    (typeof item.giftDeliveryCategory === 'object' && item.giftDeliveryCategory?.name) ||
    (typeof info?.giftDeliveryCategory === 'object' &&
      (info.giftDeliveryCategory as { name?: string })?.name) ||
    (categoryId ? categoryNameById.get(categoryId) : undefined);

  return [
    itemName(item),
    categoryName,
    info?.subCategory,
    ...(info?.tags ?? []),
    info?.couponDetail?.placeOfExchange,
    ...(info?.additionalOptions ?? []).flatMap((option) => [
      option.name,
      ...(option.choices ?? []).map((choice) => choice.name),
    ]),
    item.detailInfo || info?.detailInfo,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

/**
 * 상품 상세 축약 모달 — 고객 상세페이지와 같은 public 단건 조회를 써서
 * 선택형 선택지(choiceUnits, 이미지 포함)까지 그대로 가져온다.
 * 가격은 상조별 판매가(목록의 item)가 기준.
 * 화면 자체는 답례품 배송관리자 콘솔과 공용(GiftItemPreviewModal).
 */
const ItemDetailModal: FC<{ item: GiftItemData; onClose: () => void }> =
  function ItemDetailModal({ item, onClose }) {
    useGiftItemForStoreHook(item._id);
    const status = useRecoilValue(giftItemForStoreStatusAtomFamily(item._id));
    const detail = useRecoilValue(giftItemForStoreDataAtomFamily(item._id));

    // 상세 조회 실패 시에도 목록 데이터만으로 기본 정보는 보여준다
    const info = detail?.giftItemInfo ?? item.giftItemInfo;

    // 상세 이미지 — 고객 상세페이지와 같은 규칙: 카테고리 상세(맥락설명) 먼저,
    // 그 다음 상품 상세(상품별 오버라이드 우선 → 원상품)
    const detailImageList = useMemo(() => {
      const productImages =
        detail?.detailImages && detail.detailImages.length > 0
          ? detail.detailImages
          : detail?.giftItemInfo?.detailImages ?? [];
      const category = detail?.giftItemInfo?.giftDeliveryCategory;
      const categoryImages =
        category && typeof category === 'object' && Array.isArray(category.detailImages)
          ? (category.detailImages as Image[])
          : [];
      return [...categoryImages, ...productImages].filter(
        (img): img is Image =>
          Boolean(img) && typeof img === 'object' && 'url' in (img as object),
      );
    }, [detail]);

    const choiceUnits = useMemo(() => {
      const units = detail?.giftItemInfo?.choiceUnits;
      if (!units || !Array.isArray(units)) return [];
      return units.filter(
        (unit): unit is GiftChoiceUnitData => typeof unit === 'object' && unit !== null,
      );
    }, [detail]);

    return (
      <GiftItemPreviewModal
        onClose={onClose}
        loading={status === 'loading' || status === 'idle'}
        source={{
          name: itemName(item),
          image: item.image || item.giftItemInfo?.image,
          type: info?.type || GiftItemInfoType.Delivery,
          isSelectable: info?.isSelectable,
          isEnabled: info?.isEnabled,
          priceRegular: item.priceRegular || info?.priceRegular || 0,
          priceRetail: item.priceRetail || 0,
          detailInfo: item.detailInfo || info?.detailInfo,
          tags: info?.tags,
          minOrderQuantity: info?.minOrderQuantity,
          orderUnit: info?.orderUnit,
          couponDetail: info?.couponDetail,
          deliveryDetail: info?.deliveryDetail,
          allowIndividualDelivery: info?.allowIndividualDelivery,
          useThanksPrint: info?.useThanksPrint,
          additionalOptions: info?.additionalOptions,
          choiceUnits,
          detailImages: detailImageList,
        }}
      />
    );
  };

//* 스토어 카드(GiftItemPreview)와 같은 생김새 — 클릭하면 상세 모달
const ItemCard: FC<{ item: GiftItemData; onOpen: (item: GiftItemData) => void }> =
  function ItemCard({ item, onOpen }) {
    const info = item.giftItemInfo;
    const isCoupon = itemType(item) === GiftItemInfoType.Coupon;
    const isSelectable = info?.isSelectable || false;
    const imageUrl = itemImageUrl(item);
    const priceRegular = item.priceRegular || info?.priceRegular || 0;
    const priceRetail = item.priceRetail || 0;
    const discount = discountRate(priceRegular, priceRetail);
    const minQuantity = info?.minOrderQuantity ?? 1;
    const shippingFee = info?.deliveryDetail?.shippingFee;
    const tags = info?.tags ?? [];
    const disabled = info?.isEnabled === false;

    return (
      <div
        className={`flex cursor-pointer flex-col overflow-hidden rounded-xl bg-white shadow-md ring-1 ring-black/5 transition hover:shadow-lg ${
          disabled ? 'opacity-60' : ''
        }`}
        onClick={() => onOpen(item)}
      >
      {/* 상품 사진 + 배지 (스토어와 동일) */}
      <div className="relative">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={itemName(item)}
            className="aspect-square w-full object-cover object-center"
          />
        ) : (
          <div className="aspect-square w-full bg-gray-100" />
        )}
        <div className="absolute left-0 top-0 p-1.5">
          <span
            className={`inline-block rounded-sm px-1.5 py-0.5 text-[11px] font-bold text-white shadow-sm ${
              isSelectable
                ? 'bg-gradient-to-r from-indigo-500 to-purple-500 ring-1 ring-indigo-400/50'
                : !isCoupon
                ? 'bg-blue-500 ring-1 ring-blue-400/50'
                : 'bg-purple-500 ring-1 ring-purple-400/50'
            }`}
          >
            {isSelectable
              ? `선택형 ${isCoupon ? '쿠폰' : '배송'}`
              : !isCoupon
              ? '배송형 상품'
              : '쿠폰'}
          </span>
          {disabled && (
            <span className="ml-1 inline-block rounded-sm bg-gray-700 px-1.5 py-0.5 text-[11px] font-bold text-white shadow-sm">
              판매중지
            </span>
          )}
        </div>
        {discount > 0 && (
          <div className="absolute right-1.5 top-1.5 rounded-sm bg-red-500 px-1.5 py-0.5 text-[11px] font-bold text-white shadow-sm ring-1 ring-red-400/50">
            {discount}%
          </div>
        )}
      </div>

      {/* 상품 정보 (스토어와 동일) */}
      <div className="flex flex-1 flex-col px-2.5 pb-2.5 pt-2">
        {/* 전화 상담용이라 이름은 줄임 없이 전부 노출 */}
        <h4 className="break-keep text-sm font-bold leading-tight">
          {itemName(item)}
        </h4>
        <div className="mt-1.5 flex items-baseline gap-1">
          {discount > 0 && (
            <span className="text-sm font-extrabold text-red-500">{discount}%</span>
          )}
          <span className="text-[15px] font-extrabold">{formatPrice(priceRetail)}</span>
          <span className="text-[11px] text-gray-500">원</span>
          {priceRetail !== priceRegular && priceRegular > 0 && (
            <span className="text-[10px] text-gray-400 line-through">
              {formatPrice(priceRegular)}원
            </span>
          )}
        </div>
        {tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {tags.map((tag, i) => (
              <span
                key={`${tag}-${i}`}
                className="rounded-full bg-teal-500 px-2 py-0.5 text-[11px] font-bold text-white ring-1 ring-teal-400/50"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
        {/* 주문 조건 요약 — 고객 카드엔 없지만 상담에 바로 필요한 한 줄 */}
        <p className="mt-1.5 text-[11px] text-gray-400">
          {minQuantity > 1 ? `${minQuantity}개부터` : '1개부터'}
          {info?.orderUnit != null && info.orderUnit > 1 && ` · ${info.orderUnit}개씩`}
          {!isCoupon &&
            (shippingFee != null && shippingFee > 0
              ? ` · 배송비 ${formatPrice(shippingFee)}원`
              : ' · 배송비 없음')}
        </p>
      </div>

    </div>
  );
};

const ItemGrid: FC<{
  items: GiftItemData[];
  onOpen: (item: GiftItemData) => void;
}> = function ItemGrid({ items, onOpen }) {
  return (
    <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 xl:grid-cols-4">
      {items.map((item) => (
        <ItemCard key={item._id} item={item} onOpen={onOpen} />
      ))}
    </div>
  );
};

type TypeFilter = 'all' | 'coupon' | 'delivery';

export const GiftPhoneCatalogSection: FC = function GiftPhoneCatalogSection() {
  const { isAdmin } = useAuth();

  useGiftItemListHook(isAdmin() ? LowerRole.Admin : LowerRole.GiftAgent);
  const status = useRecoilValue(giftItemListStatusAtom);
  const itemList = useRecoilValue(giftItemListDataAtom);

  // 분류는 고객 스토어와 같은 public 카테고리 목록 (order 정렬)
  useGiftDeliveryCategoryListHook(LowerRole.Public);
  const deliveryCategoryList = useRecoilValue(giftDeliveryCategoryListDataAtom);

  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [searchText, setSearchText] = useState('');
  const [showDisabled, setShowDisabled] = useState(false);
  const [detailItem, setDetailItem] = useState<GiftItemData | null>(null);

  // 상조 목록 (상품이 있는 상조만)
  const brands = useMemo(() => {
    const map = new Map<string, string>();
    (itemList ?? []).forEach((item) => {
      const id = item.bugoBrand?._id;
      if (id && !map.has(id)) map.set(id, brandName(item));
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [itemList]);

  const activeBrandId = selectedBrandId ?? brands[0]?.id ?? null;

  // 카테고리 id → 이름 (검색용)
  const categoryNameById = useMemo(
    () => new Map(deliveryCategoryList.map((category) => [category._id, category.name])),
    [deliveryCategoryList],
  );

  // 상품별 검색 대상 텍스트 (입력마다 다시 만들지 않도록 캐시)
  const haystackById = useMemo(() => {
    const map = new Map<string, string>();
    (itemList ?? []).forEach((item) =>
      map.set(item._id, buildSearchHaystack(item, categoryNameById)),
    );
    return map;
  }, [itemList, categoryNameById]);

  // 고객 스토어와 같은 정렬: 쿠폰형 먼저, 그 안에서 표시 순서
  const visibleItems = useMemo(() => {
    // 띄어쓰기로 나눈 단어가 모두 포함된 상품만 (예: "수건 프리미엄")
    const keywords = searchText.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return (itemList ?? [])
      .filter((item) => item.bugoBrand?._id === activeBrandId)
      .filter((item) => showDisabled || item.giftItemInfo?.isEnabled !== false)
      .filter((item) => {
        if (keywords.length === 0) return true;
        const haystack = haystackById.get(item._id) ?? '';
        return keywords.every((keyword) => haystack.includes(keyword));
      })
      .sort((a, b) => {
        const typeA = itemType(a) === GiftItemInfoType.Coupon ? 0 : 1;
        const typeB = itemType(b) === GiftItemInfoType.Coupon ? 0 : 1;
        if (typeA !== typeB) return typeA - typeB;
        return itemOrder(a) - itemOrder(b);
      });
  }, [itemList, activeBrandId, searchText, showDisabled, haystackById]);

  const couponItems = useMemo(
    () => visibleItems.filter((item) => itemType(item) === GiftItemInfoType.Coupon),
    [visibleItems],
  );
  const deliveryItems = useMemo(
    () => visibleItems.filter((item) => itemType(item) === GiftItemInfoType.Delivery),
    [visibleItems],
  );

  // 배송형: 고객 스토어와 같은 카테고리 → 하위분류 그룹
  const deliverySections = useMemo(() => {
    const usedIds = new Set(
      deliveryItems.map(itemCategoryId).filter((id): id is string => Boolean(id)),
    );
    const categories = deliveryCategoryList.filter((c) => usedIds.has(c._id));
    const coveredIds = new Set(categories.map((c) => c._id));

    const sections = categories.map((category) => {
      const items = deliveryItems.filter((item) => itemCategoryId(item) === category._id);
      const subNames = category.subCategories ?? [];
      const usedSubs = subNames.filter((name) =>
        items.some((item) => item.giftItemInfo?.subCategory === name),
      );
      const groups = usedSubs.map((sub) => ({
        sub,
        items: items.filter((item) => item.giftItemInfo?.subCategory === sub),
      }));
      const ungrouped = items.filter((item) => {
        const sub = item.giftItemInfo?.subCategory;
        return !sub || !usedSubs.includes(sub);
      });
      return { category, groups, ungrouped };
    });

    const uncategorized = deliveryItems.filter((item) => {
      const id = itemCategoryId(item);
      return !id || !coveredIds.has(id);
    });

    return { sections, uncategorized };
  }, [deliveryItems, deliveryCategoryList]);

  const showCoupon = typeFilter !== 'delivery' && couponItems.length > 0;
  const showDelivery = typeFilter !== 'coupon' && deliveryItems.length > 0;

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="center-box py-16">
        <Loading />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="rounded-lg bg-white p-8 text-center text-sm text-red-600 shadow">
        {isAdmin()
          ? '상품 목록을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.'
          : '상품 목록은 서버 업데이트 후 사용할 수 있습니다.'}
      </div>
    );
  }

  if (brands.length === 0) {
    return (
      <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 shadow">
        등록된 상품이 없습니다.
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      {/* 상조 선택 */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {brands.map((brand) => (
          <button
            key={brand.id}
            onClick={() => setSelectedBrandId(brand.id)}
            className={
              brand.id === activeBrandId
                ? 'rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white'
                : 'rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50'
            }
          >
            {brand.name}
          </button>
        ))}
      </div>

      {/* 검색·필터 */}
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="relative">
          <SearchIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="상품명·분류·태그 검색"
            className="rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div className="flex gap-1">
          {(
            [
              { key: 'all', label: '전체' },
              { key: 'coupon', label: '쿠폰형' },
              { key: 'delivery', label: '배송형' },
            ] as { key: TypeFilter; label: string }[]
          ).map((chip) => (
            <button
              key={chip.key}
              onClick={() => setTypeFilter(chip.key)}
              className={
                typeFilter === chip.key
                  ? 'rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white'
                  : 'rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50'
              }
            >
              {chip.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowDisabled((prev) => !prev)}
          className={
            showDisabled
              ? 'rounded-md bg-gray-700 px-3 py-1.5 text-xs font-medium text-white'
              : 'rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50'
          }
        >
          판매중지 포함
        </button>
        <span className="text-xs text-gray-400">
          {visibleItems.length}개 · 상품을 누르면 상담 정보가 펼쳐져요
        </span>
      </div>

      {!showCoupon && !showDelivery ? (
        <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 shadow">
          조건에 맞는 상품이 없습니다.
        </div>
      ) : (
        <>
          {/* 쿠폰형 섹션 — 고객 스토어와 같은 순서/카피 */}
          {showCoupon && (
            <section>
              <div className="mb-3 flex items-baseline gap-2">
                <h2 className="text-base font-bold text-gray-900">쿠폰형 답례품</h2>
                <span className="text-xs text-gray-400">모바일 쿠폰으로 바로 전송</span>
              </div>
              <ItemGrid items={couponItems} onOpen={setDetailItem} />
            </section>
          )}

          {showCoupon && showDelivery && <div className="my-7 h-2 rounded bg-gray-200" />}

          {/* 배송형 섹션 — 카테고리 → 하위분류 순 (고객 스토어와 동일) */}
          {showDelivery && (
            <section>
              <div className="mb-3 flex items-baseline gap-2">
                <h2 className="text-base font-bold text-gray-900">배송형 답례품</h2>
                <span className="text-xs text-gray-400">원하는 주소로 배송해 드려요</span>
              </div>
              <div className="space-y-7">
                {deliverySections.sections.map(({ category, groups, ungrouped }) => (
                  <section key={category._id}>
                    <h3 className="mb-3 text-base font-bold text-gray-900">
                      {category.name}
                    </h3>
                    {ungrouped.length > 0 && (
                      <div className="mb-5">
                        <ItemGrid items={ungrouped} onOpen={setDetailItem} />
                      </div>
                    )}
                    <div className="space-y-5">
                      {groups.map(({ sub, items }) => (
                        <div key={sub}>
                          <h4 className="mb-2.5 flex items-center gap-1.5 text-sm font-semibold text-gray-500">
                            {sub}
                            <span className="text-xs font-normal text-gray-400">
                              {items.length}
                            </span>
                          </h4>
                          <ItemGrid items={items} onOpen={setDetailItem} />
                        </div>
                      ))}
                    </div>
                  </section>
                ))}
                {deliverySections.uncategorized.length > 0 && (
                  <section>
                    {deliverySections.sections.length > 0 && (
                      <h3 className="mb-3 text-base font-bold text-gray-900">기타</h3>
                    )}
                    <ItemGrid items={deliverySections.uncategorized} onOpen={setDetailItem} />
                  </section>
                )}
              </div>
            </section>
          )}
        </>
      )}

      {/* 상품 상세 축약 모달 */}
      {detailItem && (
        <ItemDetailModal item={detailItem} onClose={() => setDetailItem(null)} />
      )}
    </div>
  );
};
